<script>
import { mapGetters } from 'vuex'
import Logo from '@components/logo'
import analyticsService from '../services/analytics-service'

export default {
    components: {
        Logo,
    },
    computed: {
        ...mapGetters('auth', ['loggedIn']),
        ...mapGetters('member', ['email', 'premium', 'superAdmin']),
        isPremium() {
            return this.$store.getters['premium/isPremium']
        },
        entitledFreeTrial() {
            return !this.$store.getters['premium/hasEverBeenPremium']
        },
        isMenuOpen() {
            return this.$store.state.appearance.isMainMenuOpen
        },
        env() {
            return process.env.VUE_APP_ENV
        },
    },
    watch: {
        $route() {
            this.closeMainMenu()
        },
    },
    mounted() {
        addEventListener('resize', this.closeMainMenu, { passive: true })
    },
    beforeDestroy() {
        removeEventListener('resize', this.closeMainMenu)
    },
    methods: {
        onPremiumUpsellClick() {
            analyticsService.premiumUpsellInitiated('sidebar')
        },
        closeMainMenu() {
            this.$store.dispatch('appearance/closeMainMenu')
        },
    },
}
</script>

<template>
    <div
        :is-nav-open="isMenuOpen"
        :class="['side-bar', { 'active-nav': isMenuOpen }]"
    >
        <Logo class="logo" />

        <BaseLink
            v-if="loggedIn && !isPremium"
            :to="{ name: 'premium/view-plans', query: { from: 'sidebar' } }"
            class="premium-link"
        >
            <BaseButton
                button-type="highlight"
                size="xsmall"
                :modifiers="{ fill: true, noMargins: true }"
                @click="onPremiumUpsellClick"
            >
                {{ entitledFreeTrial ? 'START FREE PREMIUM TRIAL' : 'UPGRADE TO PREMIUM' }}
            </BaseButton>
        </BaseLink>

        <div class="section">
            <BaseLink
                class="title base-link"
                :to="{ name: 'home' }"
                name="home"
            >
                <BaseIcon
                    class="icon menu-icon icon-home"
                    name="home"
                />
                <span class="header">Home</span>
            </BaseLink>

            <BaseLink
                class="item base-link"
                :class="{ active: $route.name === 'browse/charts/index' }"
                :to="{ name: 'browse/charts/index' }"
                name="browse/charts/index"
            >
                <div class="link">Charts</div>
            </BaseLink>

            <BaseLink
                class="item base-link"
                :class="{ active: $route.name === 'browse/playlists/index' }"
                :to="{ name: 'browse/playlists/index' }"
            >
                <div class="link">Playlists</div>
            </BaseLink>

            <BaseLink
                class="item base-link"
                :class="{ active: $route.name === 'browse/genres' }"
                :to="{ name: 'browse/genres' }"
            >
                <div class="link">Genres & Moods</div>
            </BaseLink>

            <BaseLink
                class="item base-link"
                :class="{ active: ['browse/mixes/latest', 'browse/mixes/trending'].includes($route.name) }"
                :to="{ name: 'browse/mixes/latest' }"
            >
                <div class="link">Mixes</div>
            </BaseLink>
        </div>

        <div class="section section-search">
            <BaseLink
                class="title base-link"
                :to="{ name: 'search' }"
            >
                <BaseIcon
                    class="icon menu-icon"
                    name="search"
                />
                <span class="header">Search</span>
            </BaseLink>
        </div>

        <div class="section">
            <BaseLink
                class="title base-link"
                :to="{ name: 'channel-index' }"
                name="radio"
            >
                <BaseIcon
                    class="icon menu-icon"
                    name="radio"
                />
                <span class="header">Radio</span>
            </BaseLink>

            <BaseLink
                v-for="channelLink in $store.state.channels.channels"
                :key="channelLink.id"
                class="item base-link"
                :class="{ active: channelLink.id === $route.params.channelId }"
                :to="{ name: 'channel', params: { channelId: channelLink.id } }"
                name="channel"
            >
                <div class="link">{{ channelLink.title }}</div>
            </BaseLink>
        </div>

        <div class="section">
            <div
                class="title base-link"
                name="radio"
            >
                <BaseIcon
                    class="icon menu-icon"
                    name="browse"
                />
                <span class="header">Your Library</span>
            </div>

            <BaseLink
                class="item base-link"
                :class="{ active: $route.name === 'your-library/mixes' }"
                :to="{ name: 'your-library/mixes' }"
                name="channel"
            >
                <div class="link">Your Mixes</div>
            </BaseLink>

            <BaseLink
                class="item base-link"
                :class="{ active: $route.name === 'your-library/artists' }"
                :to="{ name: 'your-library/artists' }"
                name="channel"
            >
                <div class="link">Your Artists</div>
            </BaseLink>

            <BaseLink
                class="item base-link"
                :class="{ active: $route.name === 'your-library/shows' }"
                :to="{ name: 'your-library/shows' }"
                name="channel"
            >
                <div class="link">Your Shows</div>
            </BaseLink>
            <BaseLink
                :class="['item', 'baseLink', { ['active']: $route.name === 'your-library/genres' }]"
                :to="{ name: 'your-library/genres' }"
                name="channel"
            >
                <div :class="'link'">Your Genres</div>
            </BaseLink>
        </div>

        <div class="section section-account">
            <BaseLink
                v-if="!loggedIn"
                class="item base-link"
                name="login"
            >
                <div class="link">Log in</div>
            </BaseLink>

            <div v-else>
                <BaseButton
                    button-type="highlight"
                    size="xsmall"
                    :modifiers="{ fill: true, noMargins: true }"
                    @click="() => {}"
                >{{ email }}</BaseButton>

                <br >

                <BaseLink
                    class="item base-link"
                    :class="{ active: $route.name === 'account' }"
                    name="account/overview"
                >
                    <div class="link">Account Management</div>
                </BaseLink>

                <BaseLink
                    class="item base-link"
                    :class="{ active: $route.name === 'settings' }"
                    name="settings"
                >
                    <div class="link">Settings</div>
                </BaseLink>

                <BaseLink
                    class="item base-link"
                    name="logout"
                >
                    <div class="link">Log out</div>
                </BaseLink>
            </div>
        </div>
        <div class="section section-about">
            <div class="separator"/>
            <div class="title">
                <span class="header">Discover FRISKY</span>
            </div>
            <a
                class="item base-link"
                href="https://frisky.fm/about"
                target="_blank"
            >
                <div class="link">About</div>
            </a>
            <a
                class="item base-link"
                href="https://friskyfm.myshopify.com/"
                target="_blank"
            >
                <div class="link">Store</div>
            </a>
            <a
                class="item base-link"
                href="https://frisky.fm/support"
                target="_blank"
            >
                <div class="link">Support</div>
            </a>
            <a
                class="item base-link"
                href="https://frisky.fm/about/privacy-policy"
                target="_blank"
            >
                <div class="link">Privacy Policy</div>
            </a>
            <a
                class="item base-link"
                href="https://frisky.fm/about/terms"
                target="_blank"
            >
                <div class="link">Terms of Use</div>
            </a>
        </div>
    </div>
</template>

<style scoped lang="scss">
@use 'sass:math';
@use '@design' as *;

.menu-button {
    position: absolute;
    top: math.div($size-menu-bar-height, 2);
    left: 30px;
    cursor: pointer;
    transform: translateY(-50%);
}

.icon {
    width: 24px;
    height: 24px;
    margin-right: $s-xxs;
    vertical-align: middle;
    fill: $c-white;

    &-home {
        width: 20px;
        height: 20px;
    }
}

.menu-icon {
    display: none;
}

.header {
    vertical-align: middle;
}

.side-bar {
    position: fixed;
    left: 0;
    z-index: $layer-modal-z-index;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: $size-side-bar;
    height: calc(100% - #{$size-music-player + $size-menu-bar-height});
    padding: $s-xxl $s-xxl $s-xxxl * 2;
    overflow-y: auto;
    background: $c-grey-heavy-metal;
    transition: transform 0.3s ease-in-out;
    transform: translateX(-100%);

    &.active-nav {
        transform: translateX(0);
    }

    &::-webkit-scrollbar-thumb {
        background-color: transparentize($c-tuna, 0.8);
    }
}

.section {
    margin-top: $s-l;

    &-search {
        display: none;
    }
}

.logo {
    display: none;
}

.title {
    display: block;
    margin-bottom: 15px;
    font-size: 14px;
    font-weight: 900;
    line-height: 18px;
    letter-spacing: -0.5px;

    &, &:visited {
        color: $c-white;
    }
}

.link {
    display: block;
    font-size: 13px;
    font-weight: 500;
    line-height: 36px;
    color: $c-text-faded;
    text-decoration: none;
    letter-spacing: -0.09px;
    vertical-align: middle;

    &:hover {
        color: $c-pink-mandy;
    }
}

.base-link {
    &:hover,
    &:active {
        text-decoration: none;
    }
}

.item {
    display: flex;
    flex: 1 1 auto;

    &.active .link {
        font-weight: bold;
        color: $c-white;
    }

    &:last-of-type {
        margin-bottom: $s-m;
    }
}

.text-email {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.separator {
    position: relative;
    display: block;
    width: 100%;
    height: 1px;
    margin: $s-xl 0;
    background: $c-mine-shaft;
}

@include desktop {
    .premium-link {
        display: none;
    }

    .side-bar {
        position: sticky;
        top: 0;
        z-index: auto;
        width: $size-large-side-bar;
        height: calc(100vh - #{$size-music-player});
        padding-top: $s-xl;
        padding-bottom: $s-xxl;
        transform: translateX(0);
    }

    .menu-button {
        display: none;
    }

    .menu-icon {
        display: unset;
    }

    .logo {
        display: block;
        margin-bottom: $s-xl;
    }

    .item {
        &:last-of-type {
            margin-bottom: 0;
        }
    }

    .section {
        &-search {
            display: block;
        }

        &-account {
            display: none;
        }
    }
}

@media screen and (min-height: 1000px) {
    .section {

        &-about {
            position: absolute;
            bottom: $s-xxl;
        }
    }
}
</style>
